import React, { FunctionComponent, ReactNode } from 'react'
import { Heading, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import clsx from 'clsx'

import styles from './style.module.css'
import {
  useCheckoutState,
  useIsDeferredShipping,
  useIsPickup,
  useShippingOption,
  useIsShippingAddressValid,
  useShippingAddress,
  useInitialData,
  useInlineArcadeComponentsEnabled,
} from '../../state'
import { ShippingAddress, ShippingOptionError } from '../'
import { useCheckoutSizes } from '../../utils/responsive'
import { AddressModeType, ShippingAddressStatus, ShippingOptionErrorType, TerminalErrorCode } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { ShippingOptionSection } from '../ShippingOption/ShippingOptionSection'
import { ShippingOptionProvider } from '../ShippingOption/context'
import { ShippingAddressInline } from '../ShippingAddressInline/ShippingAddressInline'

interface Props {
  hasBorder?: boolean
}

export const ShippingSummary: FunctionComponent<Props> = ({ hasBorder = true }) => {
  const { initial, checkout } = useCheckoutState()
  const { error: shippingOptionError } = useShippingOption()
  const isPickup = useIsPickup()
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { textSize, sectionHeadingSize } = useCheckoutSizes()
  const isDeferredShipping = useIsDeferredShipping()
  const { shippingAddressStatus } = useShippingAddress()
  const isValidShippingAddress = useIsShippingAddressValid()
  const { addressMode } = useInitialData()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isInlineArcadeComponentEnabled = useInlineArcadeComponentsEnabled()

  const hasValidShippingAddress = isValidShippingAddress || shippingAddressStatus < ShippingAddressStatus.Initialised
  const hasPickupShippingOptionError =
    isPickup &&
    shippingOptionError !== null &&
    shippingOptionError !== undefined &&
    shippingOptionError !== ShippingOptionErrorType.ShippingAddressIncomplete &&
    shippingOptionError !== ShippingOptionErrorType.ShippingOptionUnableToUpdate
  const isPickupAddressInvalid = initial.pickup && (checkout?.shippingAddress === null || checkout?.shippingAddress === undefined)
  const showPickupError = isPickupAddressInvalid || hasPickupShippingOptionError

  const pickupError = shippingOptionError === ShippingOptionErrorType.TotalTooHighToLoadAnyOption ? shippingOptionError : TerminalErrorCode.InvalidPickupAddress

  if (shippingOptionError === ShippingOptionErrorType.ShippingAddressIncomplete) {
    logEvent(TrackingEvent.VIEWED_INCOMPLETE_SHIPPING_ADDRESS_ERROR)
  }

  const renderHeading = (): ReactNode | null => {
    if (isBronteDesignLanguage && addressMode !== AddressModeType.NO_ADDRESS) {
      return <Heading size={isDeferredShipping ? sectionHeadingSize : textSize}>{isPickup ? t('common:pickupFrom') : t('common:shipTo')}</Heading>
    }

    if (showPickupError && !isBronteDesignLanguage && addressMode !== AddressModeType.NO_ADDRESS) {
      return (
        <div className={styles.cashHeadingContainer}>
          <Heading size={sectionHeadingSize}>
            {isPickup ? t('common:pickupFrom', { context: isInlineArcadeComponentEnabled ? 'inline' : '' }) : t('common:shipTo')}
          </Heading>
        </div>
      )
    }

    return null
  }

  const renderBody = (): ReactNode => {
    if (showPickupError) {
      const containerClass = clsx(isInlineArcadeComponentEnabled && styles.inlineErrorContainer)
      return (
        <div className={containerClass}>
          <ShippingOptionError error={pickupError} isPickup />
        </div>
      )
    }

    return (
      <>
        {isInlineArcadeComponentEnabled ? <ShippingAddressInline /> : <ShippingAddress />}
        {initial.shippingOptionRequired && hasValidShippingAddress && (
          <ShippingOptionProvider>
            <ShippingOptionSection />
          </ShippingOptionProvider>
        )}
      </>
    )
  }

  return (
    <div className={clsx({ [styles.container]: !isDeferredShipping && hasBorder && isBronteDesignLanguage })}>
      {renderHeading()}
      {renderBody()}
    </div>
  )
}
